import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/cn";
import { BLOCK_SURFACE } from "./blockSurface";
import { Input } from "../../primitives/Input";
import { Textarea } from "../../primitives/Textarea";
import { Checkbox } from "../../primitives/Checkbox";
import { Label } from "../../primitives/Label";
import { Button } from "../../primitives/Button";
import { BlockReceiptView, readReceipt, type BlockReceipt } from "./blockReceipt";

/**
 * Form - a small structured-input form the model can ask the user to fill.
 * Renderer for the K2 `form` block (`docs/contracts/block-registry.md`): the
 * platform's chat `BlockRenderer` passes a `form` block's `props` straight
 * through, so the prop names match the block payload exactly. INTERACTIVE.
 *
 * Block props (`{ title?, description?, fields, submitLabel?, submit_to,
 * receipt? }`):
 *   - `title?` / `description?` - optional header.
 *   - `fields` - the inputs, each
 *     `{ name, label, type, placeholder?, required?, options?, defaultValue? }`,
 *     where `type` is `"text" | "textarea" | "select" | "checkbox" | "email" |
 *     "number"`. `select` reads `options: [{ value, label }]`.
 *   - `submitLabel?` - the submit button copy. Defaults to "Submit".
 *   - `submit_to` - destination routing token, carried on the submit event.
 *   - `receipt?` - once submitted, the durable settled state
 *     (`{ outcome, summary, identifiers?, at }`). When present, the settled
 *     receipt renders instead of the live form - so a history reload shows what
 *     was submitted rather than a fresh, re-submittable control. This is the
 *     bug the receipt backfill fixes.
 *
 * On submit the block emits the shared block-submit event keyed by `block_id` +
 * the collected `values` (see `blockSubmit.tsx`).
 *
 * Client-only props (never part of the streamed block): `className`, `onSubmit`
 * (wired by the block adapter to the submit context), `disabled`.
 *
 * Composed from the `Input` / `Textarea` / `Checkbox` / `Label` / `Button`
 * primitives. Token-themed throughout with no hardcoded colors. SSR-safe: field
 * state initialises from `defaultValue` in component state, never at module top.
 */

/** A form field's input type. */
export type FormFieldType = "text" | "textarea" | "select" | "checkbox" | "email" | "number";

export interface FormFieldOption {
  value: string;
  label: string;
}

export interface FormField {
  /** Field key, used in the emitted `values` record. */
  name: string;
  /** Visible label. */
  label: string;
  /** Input type. */
  type: FormFieldType;
  /** Placeholder for text-like fields. */
  placeholder?: string;
  /** Whether the field is required. */
  required?: boolean;
  /** Options for a `select` field. */
  options?: FormFieldOption[];
  /** Initial value. */
  defaultValue?: string | boolean;
}

const formVariants = cva(
  cn("not-prose flex flex-col gap-4", BLOCK_SURFACE),
);

/** Build the initial field-values record from each field's defaultValue. */
function initialValues(fields: FormField[]): Record<string, string | boolean> {
  const out: Record<string, string | boolean> = {};
  for (const f of fields) {
    out[f.name] = f.defaultValue ?? (f.type === "checkbox" ? false : "");
  }
  return out;
}

export interface FormProps
  extends
    Omit<React.FormHTMLAttributes<HTMLFormElement>, "onSubmit" | "title">,
    VariantProps<typeof formVariants> {
  /** Optional header title. */
  title?: string;
  /** Optional header description. */
  description?: string;
  /** The fields. Required. */
  fields: FormField[];
  /** Submit button copy. Defaults to "Submit". */
  submitLabel?: string;
  /** Settled state - renders the receipt instead of the live form when present. */
  receipt?: BlockReceipt | unknown;
  /** Submit handler. Wired by the block adapter to the submit context. */
  onSubmit?: (values: Record<string, string | boolean>) => void;
  /** Disable the whole form (e.g. while a submit is in flight). */
  disabled?: boolean;
}

export const Form = React.forwardRef<HTMLFormElement, FormProps>(function Form(
  {
    className,
    title,
    description,
    fields,
    submitLabel = "Submit",
    receipt,
    onSubmit,
    disabled = false,
    ...rest
  },
  ref,
) {
  const settled = readReceipt(receipt);
  const [values, setValues] = React.useState<Record<string, string | boolean>>(() =>
    initialValues(fields),
  );

  const setField = React.useCallback((name: string, value: string | boolean) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (disabled) return;
    onSubmit?.(values);
  };

  if (settled) {
    return (
      <div className={cn(formVariants(), className)} data-slot="form" data-settled="true">
        {title ? (
          <p className="text-sm font-semibold leading-snug text-foreground">{title}</p>
        ) : null}
        <BlockReceiptView receipt={settled} />
      </div>
    );
  }

  return (
    <form
      ref={ref}
      className={cn(formVariants(), className)}
      data-slot="form"
      onSubmit={handleSubmit}
      {...rest}
    >
      {title || description ? (
        <div className="flex flex-col gap-1">
          {title ? (
            <p className="text-sm font-semibold leading-snug text-foreground">{title}</p>
          ) : null}
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
      ) : null}

      <div className="flex flex-col gap-3.5">
        {fields.map((field) => (
          <FormFieldRow
            key={field.name}
            field={field}
            value={values[field.name]}
            disabled={disabled}
            onChange={setField}
          />
        ))}
      </div>

      <div className="flex justify-end">
        <Button type="submit" size="sm" disabled={disabled}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
});

Form.displayName = "Form";

/* ============================================================================
 * Internal - one field row
 * ========================================================================= */

function FormFieldRow({
  field,
  value,
  disabled,
  onChange,
}: {
  field: FormField;
  value: string | boolean;
  disabled: boolean;
  onChange: (name: string, value: string | boolean) => void;
}) {
  const id = `form-field-${field.name}`;

  if (field.type === "checkbox") {
    return (
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={Boolean(value)}
          disabled={disabled}
          required={field.required}
          onCheckedChange={(checked) => onChange(field.name, checked === true)}
        />
        <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
          {field.label}
          {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
        </Label>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>
        {field.label}
        {field.required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {field.type === "textarea" ? (
        <Textarea
          id={id}
          value={String(value)}
          placeholder={field.placeholder}
          required={field.required}
          disabled={disabled}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      ) : field.type === "select" ? (
        <select
          id={id}
          value={String(value)}
          required={field.required}
          disabled={disabled}
          onChange={(e) => onChange(field.name, e.target.value)}
          className={cn(
            "h-10 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground",
            "transition-colors focus-visible:border-ring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        >
          <option value="" disabled>
            {field.placeholder ?? "Select..."}
          </option>
          {(field.options ?? []).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      ) : (
        <Input
          id={id}
          type={field.type === "email" ? "email" : field.type === "number" ? "number" : "text"}
          value={String(value)}
          placeholder={field.placeholder}
          required={field.required}
          disabled={disabled}
          onChange={(e) => onChange(field.name, e.target.value)}
        />
      )}
    </div>
  );
}

export { formVariants };
export type FormVariantProps = VariantProps<typeof formVariants>;
